/**
 * Shared HTTP status → error category classifier for GitHub's fetch paths.
 *
 * Both `@github-ui/react-core`'s `responseErrorForStatus` and
 * `@github-ui/fetch-graphql`'s `assertNoHttpErrors` turn a non-OK HTTP response
 * into a named `Error` subclass whose `.name` matches the expected-error
 * allowlist in `EXPECTED_NETWORK_ERROR_TYPES` (see `./failbot`), so failbot can
 * silence expected/non-actionable statuses instead of reporting them to Sentry.
 *
 * This module owns only the pure status → category decision. Each package maps
 * the returned category to its own `Error` class hierarchy, so "which statuses
 * are expected" and "which names are silenced" stay a single source of truth.
 */
export type HttpErrorCategory =
  | 'rate-limit'
  | 'service-unavailable'
  | 'not-acceptable'
  | 'sec-fetch'
  | 'noise'
  | 'generic'

export type ClassifyHttpErrorInput = {
  /** HTTP status code of the response. */
  status: number
  /** Value of the `X-Reject-Reason` header, if present. */
  rejectReason?: string | null
  /**
   * Response body text, when it has already been read. Omit it for lazy paths
   * that avoid reading the body; body-dependent branches then fall through to
   * `'generic'`.
   */
  bodyText?: string
}

/**
 * Maps an HTTP status (plus a couple of header/body signals) to the most
 * specific error category. Pure and `Response`-free, so it works for both eager
 * (fetch-graphql) and lazy (react-core) body reads. When `bodyText` is absent,
 * the body-dependent branches (Cloudflare 403, sec-fetch 422) fall through to
 * `'generic'`.
 */
export function classifyHttpError({status, rejectReason, bodyText}: ClassifyHttpErrorInput): HttpErrorCategory {
  switch (status) {
    case 429:
      return 'rate-limit'
    case 503:
      return 'service-unavailable'
    case 406:
      return 'not-acceptable'
    // 410 Gone and 418 are non-actionable environmental noise
    // (deprecated endpoints, bots).
    case 410:
    case 418:
      return 'noise'
  }

  if (status === 403) {
    // Cloudflare bot-protection blocks on custom domains pointed at GitHub.
    // Checked before the CSRF header to preserve fetch-graphql's original
    // ordering (isNoiseError ran first). The react-core path never passes a
    // body for a CSRF 403, so CSRF still wins there — see responseErrorForStatus.
    if (bodyText?.toLowerCase().includes('cloudflare')) {
      return 'noise'
    }
    // UI Service CSRF rejections surface via the X-Reject-Reason header.
    if (rejectReason === 'csrf') {
      return 'sec-fetch'
    }
  }

  if (status === 422 && (bodyText?.includes('sec-fetch-dest') || bodyText?.includes('sec-fetch-site'))) {
    return 'sec-fetch'
  }

  return 'generic'
}
